import { useCallback, useMemo } from 'react';
import { CbDataRequestInit, NonThrowingFetch } from '@cbhq/data-layer/network';

import { TwoFactorTargetConfigMapping } from '../config';
import { Target } from '../types';

export type NonThrowingFetchWithHeaders = (
  url: string,
  init?: CbDataRequestInit,
) => ReturnType<NonThrowingFetch>;

type UrlOverrideMiddleware = (next: NonThrowingFetch) => NonThrowingFetchWithHeaders;

type Override = () => string | undefined;

export function useBaseUrlMiddleware(
  target: Target,
  override?: Override | string,
): UrlOverrideMiddleware {
  const baseUrl = useMemo(() => {
    if (target === 'local') {
      return window.location.origin;
    }

    const overrideUrl = typeof override === 'function' ? override() : override;

    return overrideUrl ?? TwoFactorTargetConfigMapping[target].lccHost;
  }, [override, target]);

  const middleware: UrlOverrideMiddleware = useCallback(
    (next: NonThrowingFetch): NonThrowingFetchWithHeaders => {
      return async (url: string, init?: CbDataRequestInit) => {
        if (!baseUrl) {
          return next(url, init);
        }

        const oldUrl = new URL(url, baseUrl);
        const newUrl = oldUrl.toString().replace(oldUrl.origin, baseUrl);

        return next(newUrl, init);
      };
    },
    [baseUrl],
  );

  return middleware;
}
